import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const getAdminToken = () =>
  (typeof sessionStorage !== 'undefined' && sessionStorage.getItem('admin_token')) || '';
export const adminAuthHeader = () => ({ Authorization: `Bearer ${getAdminToken()}` });

// --- MITA REG-01 / REG-02 + Phase-1 auth prerequisite ---------------------
// Centralize the admin bearer so EVERY admin API call carries it, instead of
// each call site having to remember `headers: adminAuthHeader()` (most did not
// — which is why authenticated Calendar / Seating / Event-Program writes
// returned 401 once those endpoints required auth). Scoped to our own backend
// origin and gated on an active admin session, so guest-facing pages never
// attach an admin token. Idempotent: never overwrites an Authorization header a
// call set explicitly. Wrapped in try/catch so header wiring can never block a
// request. This is the FE half of the security work; backend enforcement
// (SEC-01/03/04) is flipped only after this ships, so nothing 401s in between.
axios.interceptors.request.use((config) => {
  try {
    const url = String(config.url || '');
    const isOurApi = url.startsWith(API) || (!!BACKEND_URL && url.startsWith(`${BACKEND_URL}/`));
    const authed = typeof sessionStorage !== 'undefined' && sessionStorage.getItem('admin_authed') === '1';
    if (isOurApi && authed) {
      config.headers = config.headers || {};
      if (!config.headers.Authorization) {
        config.headers.Authorization = `Bearer ${getAdminToken()}`;
      }
    }
  } catch (_) { /* never block a request on auth-header wiring */ }
  return config;
});

// Mirror the interceptor for the admin fetch() surface. Many admin reads/writes
// call window.fetch directly (vendor-attachment upload/delete, dashboard
// attention-items, provisional-guests, settings upload, finance export, …) and
// would otherwise carry no bearer — so once backend enforcement (SEC-01/03/04)
// is on they would 401. Same scope + session gate + idempotency as the axios
// interceptor: only our backend origin, only an authed admin session, never
// overwrite an Authorization a caller set. Patched once; SSR-guarded.
if (typeof window !== 'undefined' && typeof window.fetch === 'function' && !window.__mvitationsAuthFetch) {
  const _origFetch = window.fetch.bind(window);
  window.fetch = (input, init) => {
    try {
      const url = typeof input === 'string' ? input : (input && input.url) || '';
      const isOurApi = url.startsWith(API) || (!!BACKEND_URL && url.startsWith(`${BACKEND_URL}/`));
      const authed = typeof sessionStorage !== 'undefined' && sessionStorage.getItem('admin_authed') === '1';
      if (isOurApi && authed) {
        const headers = new Headers((init && init.headers) || (typeof input !== 'string' && input && input.headers) || {});
        if (!headers.has('Authorization')) {
          headers.set('Authorization', `Bearer ${getAdminToken()}`);
          init = { ...(init || {}), headers };
        }
      }
    } catch (_) { /* never block a fetch on auth-header wiring */ }
    return _origFetch(input, init);
  };
  window.__mvitationsAuthFetch = true;
}

// Clear the admin session markers. Call on logout so a later same-tab guest
// navigation can never carry a stale admin bearer (closes the no-logout-clear
// footgun) and so SEC-03 token hygiene holds.
export const clearAdminSession = () => {
  try {
    sessionStorage.removeItem('admin_authed');
    sessionStorage.removeItem('admin_token');
  } catch (_) { /* ignore */ }
};

// Auth
export const adminLogin = (password) => axios.post(`${API}/admin/login`, { password }).then(r => r.data);

// Guests
export const registerGuest = (data) => axios.post(`${API}/guests`, data).then(r => r.data);
export const getGuests = (search, filter) => {
  const params = {};
  if (search) params.search = search;
  if (filter && filter !== 'all') params.filter = filter;
  return axios.get(`${API}/guests`, { params }).then(r => r.data);
};
export const getDeletedGuests = () => axios.get(`${API}/guests/deleted`).then(r => r.data);
export const updateGuest = (id, data) => axios.patch(`${API}/guests/${id}`, data).then(r => r.data);
export const softDeleteGuest = (id) => axios.delete(`${API}/guests/${id}`).then(r => r.data);
export const restoreGuest = (id) => axios.post(`${API}/guests/${id}/restore`).then(r => r.data);
export const permanentDeleteGuest = (id) => axios.delete(`${API}/guests/${id}/permanent`).then(r => r.data);
export const adminAddGuest = (data) => axios.post(`${API}/guests/add`, data).then(r => r.data);
export const mergeGuests = (primaryId, secondaryId) => axios.post(`${API}/guests/merge`, { primary_id: primaryId, secondary_id: secondaryId }).then(r => r.data);
export const importGuests = (guests) => axios.post(`${API}/guests/import`, { guests }).then(r => r.data);

// Messages
export const createMessage = (data) => axios.post(`${API}/messages`, data).then(r => r.data);
export const getMessages = (statusFilter) => {
  const params = {};
  if (statusFilter) params.status_filter = statusFilter;
  return axios.get(`${API}/messages`, { params }).then(r => r.data);
};
export const getMessageStats = () => axios.get(`${API}/messages/stats`).then(r => r.data);
export const updateMessage = (id, data) => axios.patch(`${API}/messages/${id}`, data).then(r => r.data);
export const deleteMessage = (id) => axios.delete(`${API}/messages/${id}`).then(r => r.data);

// Email Templates
export const getEmailTemplates = () => axios.get(`${API}/email-templates`).then(r => r.data);
export const createEmailTemplate = (data) => axios.post(`${API}/email-templates`, data).then(r => r.data);
export const updateEmailTemplate = (id, data) => axios.put(`${API}/email-templates/${id}`, data).then(r => r.data);
export const activateEmailTemplate = (id) => axios.patch(`${API}/email-templates/${id}/activate`).then(r => r.data);
export const deleteEmailTemplate = (id) => axios.delete(`${API}/email-templates/${id}`).then(r => r.data);
// Pass 5 / M14 — governance update (is_milo_eligible / audience_scope / timing_window).
export const updateEmailTemplateGovernance = (id, patch) => axios.patch(`${API}/email-templates/${id}/governance`, patch).then(r => r.data);
// Pass 5 / M16 — read-only constants from the shared rules layer.
export const getSystemRules = () => axios.get(`${API}/system-rules`).then(r => r.data);
// Pass 7 / O2 — Oscar general guidance CRUD (operator-tier gated on writes).
export const getOscarGuidance = (params = {}) => axios.get(`${API}/oscar/general-guidance`, { params }).then(r => r.data);
export const getOscarGuidanceCategories = () => axios.get(`${API}/oscar/general-guidance/categories`).then(r => r.data);
export const createOscarGuidance = (data) => axios.post(`${API}/oscar/general-guidance`, data).then(r => r.data);
export const updateOscarGuidance = (id, patch) => axios.patch(`${API}/oscar/general-guidance/${id}`, patch).then(r => r.data);
export const deleteOscarGuidance = (id) => axios.delete(`${API}/oscar/general-guidance/${id}`).then(r => r.data);
// Corrective pre-apply pass — separation events read surface.
export const getMiloSeparationEvents = (limit = 50) => axios.get(`${API}/milo/separation-events`, { params: { limit } }).then(r => r.data);

// SMS Templates
export const getSmsTemplates = () => axios.get(`${API}/sms-templates`).then(r => r.data);
export const createSmsTemplate = (data) => axios.post(`${API}/sms-templates`, data).then(r => r.data);
export const updateSmsTemplate = (id, data) => axios.put(`${API}/sms-templates/${id}`, data).then(r => r.data);
export const deleteSmsTemplate = (id) => axios.delete(`${API}/sms-templates/${id}`).then(r => r.data);

// Communications Intelligence — template import (scan / upload). Stores the
// uploaded content as a new template; data = { name, type:'email'|'sms', html?, text?, source? }.
export const importCommunicationTemplate = (data) => axios.post(`${API}/communications/templates/import`, data).then(r => r.data);

// Email/SMS Sending
export const sendEmails = (templateId, guestIds, cc, bcc, confirmBulkSend = false, options = {}) => axios.post(`${API}/emails/send?template_id=${templateId}`, {
  guest_ids: guestIds,
  cc_emails: cc || [],
  bcc_emails: bcc || [],
  confirm_bulk_send: confirmBulkSend,
  correlation_id: options.correlation_id || options.correlationId,
  preview_digest: options.preview_digest || options.previewDigest,
  source: options.source,
  custom_subject: options.custom_subject || options.customSubject,
  custom_body: options.custom_body || options.customBody,
  custom_from_name: options.custom_from_name || options.customFromName,
  custom_from_email: options.custom_from_email || options.customFromEmail,
}).then(r => r.data);
export const sendSms = (templateId, guestIds, options = {}) => axios.post(`${API}/sms/send-to-guests`, {
  guest_ids: guestIds,
  template_id: templateId,
  custom_body: options.custom_body || options.customBody,
  correlation_id: options.correlation_id || options.correlationId,
  preview_digest: options.preview_digest || options.previewDigest,
  source: options.source,
}).then(r => r.data);
export const previewOutboundEmail = (data) => axios.post(`${API}/communications/outbound/preview`, data).then(r => r.data);
export const previewOutboundSms = (data) => axios.post(`${API}/communications/outbound/sms/preview`, data).then(r => r.data);
export const getOutboundSettings = () => axios.get(`${API}/communications/outbound/settings`).then(r => r.data);
// Celebration config — used by the Create Communication LIVE PREVIEW to resolve
// celebration merge tags with the same values the backend substitutes at send
// time. (getEventConfig already exists below.)
export const getCelebrationConfig = () => axios.get(`${API}/celebration/config`).then(r => r.data);
export const updateOutboundSettings = (data) => axios.put(`${API}/communications/outbound/settings`, data).then(r => r.data);
export const getRsvpPendingAudience = (params = {}) => axios.get(`${API}/communications/audiences/rsvp-pending`, { params }).then(r => r.data);
export const getCommunicationsOutbox = (params = {}) => axios.get(`${API}/communications/outbox`, { params }).then(r => r.data);
export const getOutboundMessage = (id) => axios.get(`${API}/communications/outbox/${id}`).then(r => r.data);
export const approveOutboundMessage = (id) => axios.post(`${API}/communications/outbox/${id}/approve`).then(r => r.data);
export const retryOutboundMessage = (id) => axios.post(`${API}/communications/outbox/${id}/retry`).then(r => r.data);
export const cancelOutboundMessage = (id, reason) => axios.post(`${API}/communications/outbox/${id}/cancel`, { reason }).then(r => r.data);
export const generateJourneyHeldQueue = (stepId, data) => axios.post(`${API}/communications/journey/${stepId}/generate-held-queue`, data).then(r => r.data);
export const getJourneySteps = () => axios.get(`${API}/journey/steps`).then(r => r.data);
export const createJourneyStep = (data) => axios.post(`${API}/journey/steps`, data).then(r => r.data);
export const updateJourneyStep = (id, data) => axios.put(`${API}/journey/steps/${id}`, data).then(r => r.data);
export const reorderJourneySteps = (steps) => axios.put(`${API}/journey/steps/reorder`, { steps }).then(r => r.data);

// Logs (bare getEmailLogs/getSmsLogs list wrappers were unused — SentHistoryTab
// calls /email-logs and /sms-logs directly via axios — and were removed; the
// stats wrappers below are used by the Analytics screen).
export const getEmailLogStats = () => axios.get(`${API}/email-logs/stats`).then(r => r.data);
export const getSmsLogStats = () => axios.get(`${API}/sms-logs/stats`).then(r => r.data);

// Attention
export const getAttentionItems = () => axios.get(`${API}/attention`).then(r => r.data);

// Settings
export const getSettings = () => axios.get(`${API}/settings`).then(r => r.data);
export const updateSettings = (data) => axios.put(`${API}/settings`, data).then(r => r.data);

// Event config — the source of truth for event identity (host name/salutation,
// honoree, event name, date). Distinct from /settings (which only holds bcc +
// auto-reply template). Surfaces like Event Readiness read this for the greeting.
export const getEventConfig = () => axios.get(`${API}/event/config`).then(r => r.data);
// Canonical guest KPIs (PROTECTED KPI LOGIC — consume verbatim, never re-derive client-side).
export const getDashboardStats = () => axios.get(`${API}/dashboard/stats`).then(r => r.data);

// Chat
// `history` (optional) carries recent display turns so Oscar keeps context on
// short follow-ups ("yes", "please do"). Trimmed to the last 6 turns and each
// text capped at 500 chars before sending. Backward compatible — when omitted,
// the request body is unchanged from the original stateless call.
export const chatWithOscar = (message, sessionId, history = []) => {
  const trimmed = Array.isArray(history)
    ? history
        .slice(-6)
        .map((t) => ({
          role: t && t.role === 'assistant' ? 'assistant' : 'user',
          text: String((t && t.text) || '').slice(0, 500),
        }))
        .filter((t) => t.text)
    : [];
  const payload = { message, session_id: sessionId };
  if (trimmed.length) payload.history = trimmed;
  return axios.post(`${API}/chat`, payload).then((r) => r.data);
};

// Event Readiness — Vision Consultation (Phase 1)
export const getEventIntent = () => axios.get(`${API}/event-readiness/intent`).then(r => r.data);
export const startConsultation = () => axios.post(`${API}/event-readiness/intent/start`).then(r => r.data);
export const submitConsultationAnswer = (dimension, answer) => axios.post(`${API}/event-readiness/intent/answer`, { dimension, answer }).then(r => r.data);
export const advanceConsultation = () => axios.post(`${API}/event-readiness/intent/advance`).then(r => r.data);
export const backConsultation = () => axios.post(`${API}/event-readiness/intent/back`).then(r => r.data);
export const completeConsultation = () => axios.post(`${API}/event-readiness/intent/complete`).then(r => r.data);
export const refineConsultation = () => axios.post(`${API}/event-readiness/intent/refine`).then(r => r.data);

// Event Readiness · Operations Queue (v6.3)
export const listOperations = (filters = {}) => axios.get(`${API}/event-readiness/operations`, { params: filters }).then(r => r.data);
export const createOperation = (data) => axios.post(`${API}/event-readiness/operations`, data).then(r => r.data);
export const parseQuickAdd = (text) => axios.post(`${API}/event-readiness/operations/parse`, { text }).then(r => r.data);
export const updateOperation = (id, data) => axios.patch(`${API}/event-readiness/operations/${id}`, data).then(r => r.data);
export const snoozeOperation = (id, until) => axios.post(`${API}/event-readiness/operations/${id}/snooze`, { until }).then(r => r.data);
export const completeOperation = (id) => axios.post(`${API}/event-readiness/operations/${id}/complete`).then(r => r.data);
export const acceptMiloSuggestion = (id) => axios.post(`${API}/event-readiness/operations/${id}/accept`).then(r => r.data);
export const dismissOperation = (id) => axios.post(`${API}/event-readiness/operations/${id}/dismiss`).then(r => r.data);
export const deleteOperation = (id) => axios.delete(`${API}/event-readiness/operations/${id}`).then(r => r.data);
// Mirror a dated to-do onto the Calendar (shared with Calendar Intelligence).
export const createCalendarEvent = (data) => axios.post(`${API}/calendar/events`, data).then(r => r.data);
export const listCalendarEvents = (source) => axios.get(`${API}/calendar/events`, { params: source ? { source } : {} }).then(r => r.data);

// Event Readiness · Plan intelligence (Phases 1–2)
export const planIntake = (data) => axios.post(`${API}/event-readiness/plan/intake`, data).then(r => r.data);
export const planGenerate = () => axios.post(`${API}/event-readiness/plan/generate`).then(r => r.data);
export const planGapAction = (signal_key, action = 'add') =>
  axios.post(`${API}/event-readiness/plan/gap`, { signal_key, action }).then(r => r.data);
export const assistOperation = (id) =>
  axios.post(`${API}/event-readiness/operations/${id}/assist`).then(r => r.data);
export const getIntelligence = () =>
  axios.get(`${API}/event-readiness/intelligence`).then(r => r.data);
export const getImpact = (lever = 'guests', delta = 20) =>
  axios.get(`${API}/event-readiness/impact`, { params: { lever, delta } }).then(r => r.data);
export const listDecisions = () =>
  axios.get(`${API}/event-readiness/decisions`).then(r => r.data);
export const createDecision = (data) =>
  axios.post(`${API}/event-readiness/decisions`, data).then(r => r.data);
export const deleteDecision = (id) =>
  axios.delete(`${API}/event-readiness/decisions/${id}`).then(r => r.data);

// Ambient proactive Milo — the briefing (the few things Milo surfaces first).
export const getMiloBriefing = () =>
  axios.get(`${API}/event-readiness/milo/briefing`).then(r => r.data);
export const dismissMiloNudge = (key) =>
  axios.post(`${API}/event-readiness/milo/nudge/dismiss`, { key }).then(r => r.data);
export const getMiloPrefs = () =>
  axios.get(`${API}/event-readiness/milo/prefs`).then(r => r.data);
export const setMiloQuiet = (quiet) =>
  axios.post(`${API}/event-readiness/milo/prefs`, { quiet }).then(r => r.data);

// Milo — Claude-powered platform agent (Ask Milo Anything).
// Read-only platform tools answer data questions from the single source of
// truth; web search is gated — when the response has needs_web_confirmation,
// ask the host, then call again with { confirmWebSearch: true }.
export const askMiloAgent = (question, opts = {}) =>
  axios.post(`${API}/milo/agent`, {
    question,
    confirm_web_search: !!opts.confirmWebSearch,
    history: opts.history || null,
    model: opts.model || null,
  }, { headers: adminAuthHeader() }).then(r => r.data);

// v6.4 — Living Brief + Master Readiness derivation
export const getLivingBrief = () => axios.get(`${API}/event-readiness/brief`).then(r => r.data);
// Phase 2 — bidirectional module ↔ operation awareness. Module pages call
// this to know which entities have open operations attached.
export const getModuleAwareness = (module) =>
  axios.get(`${API}/event-readiness/module-awareness`, { params: { module } }).then(r => r.data);
// Phase 4 — seven-state category management. Updates a single category's state
// (and/or notes) on event_intent.category_states.
export const updateCategoryState = (key, payload) =>
  axios.patch(`${API}/event-readiness/intent/categories/${encodeURIComponent(key)}`, payload).then(r => r.data);
// Phase 5 — weekly digest scaffolding. Read-only composition; never sends.
export const getWeeklyDigest = () =>
  axios.get(`${API}/event-readiness/digest/weekly`).then(r => r.data);

// ===================================================================
// Communications Intelligence (NEW, flagged module).
// Read-only intel surfaces + VIP marking. All reuse the admin bearer
// header pattern used by other admin calls. Each is a thin wrapper so the
// new module never duplicates axios/URL wiring.
// ===================================================================
const CI_AUTH = () => ({ headers: adminAuthHeader() });

// KPI band (guests engaged / response rate / avg response time / VIPs need attention).
export const getCommsIntelKpis = () =>
  axios.get(`${API}/communications/intel/kpis`, CI_AUTH()).then(r => r.data);
// Milo next-best-action recommendation (incl. the "why" + confidence + low_confidence).
export const getCommsIntelRecommendation = () =>
  axios.get(`${API}/communications/intel/recommendation`, CI_AUTH()).then(r => r.data);
// Per-guest insights (engagement score + factors, attendance likelihood + confidence,
// sentiment, real activity timeline).
export const getCommsIntelGuestInsights = (guestId) =>
  axios.get(`${API}/communications/intel/guest/${guestId}/insights`, CI_AUTH()).then(r => r.data);
// Mark / unmark a guest as VIP (additive write — only touches is_vip).
export const setGuestVip = (guestId, isVip = true) =>
  axios.post(`${API}/guests/${guestId}/vip`, { is_vip: isVip }, CI_AUTH()).then(r => r.data);
// Journey "X of Y reached" per milestone. Read-only; returns
// { steps:[{step_id, name, channel, order, reached, total}], total_eligible_guests }.
// reached = distinct guests with a sent/delivered outbound stamped for that step.
export const getJourneyStepCompletion = () =>
  axios.get(`${API}/communications/intel/journey/step-completion`, CI_AUTH()).then(r => r.data);

// Static URLs — served from public/ folder for standard browser playback
export const INTRO_VIDEO_URL = `${process.env.PUBLIC_URL}/videos/intro_video.mp4`;
export const MAIN_VIDEO_URL = `${process.env.PUBLIC_URL}/videos/video.mp4`;
export const OSCAR_AVATAR_URL = `${process.env.PUBLIC_URL}/oscar-tile.webp`;
export const LOGO_URL = `${process.env.PUBLIC_URL}/images/mvitations_logo.png`;
export const LOGO_URL_SMALL = `${process.env.PUBLIC_URL}/images/mvitations_logo_small.png`;
export const LOGO_URL_ICON = `${process.env.PUBLIC_URL}/images/mvitations_icon.png`;
// Legacy logo for backwards compatibility
export const LEGACY_LOGO_URL = `${process.env.PUBLIC_URL}/images/logo.png`;
